import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface ExpenseCategory {
  id: string;
  restaurant_id: string;
  name: string;
  is_active: boolean;
  chart_account_id: string | null;
}

export interface CreateExpenseCategoryInput {
  name: string;
  chart_account_id?: string | null;
}

export interface UpdateExpenseCategoryInput {
  name?: string;
  is_active?: boolean;
  chart_account_id?: string | null;
}

export function useExpenseCategories() {
  const { activeRestaurant } = useRestaurantContext();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [allCategories, setAllCategories] = useState<ExpenseCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!activeRestaurant) {
      setCategories([]);
      setAllCategories([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("expense_categories" as any)
        .select("id, restaurant_id, name, is_active, chart_account_id")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (error) throw error;
      const all = ((data as unknown) as ExpenseCategory[]) || [];
      setAllCategories(all);
      setCategories(all.filter((c) => c.is_active));
    } catch (e) {
      console.error("Error fetching expense categories", e);
      toast.error("Erro ao carregar categorias de despesa");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = useCallback(
    async (input: CreateExpenseCategoryInput): Promise<string | null> => {
      if (!activeRestaurant) return null;

      try {
        const { data, error } = await supabase
          .from("expense_categories" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            name: input.name,
            chart_account_id: input.chart_account_id || null,
            is_active: true,
          })
          .select("id")
          .single();

        if (error) throw error;

        toast.success("Categoria criada com sucesso");
        await fetchCategories();
        return (data as any)?.id || null;
      } catch (e) {
        console.error("Error creating expense category", e);
        toast.error("Erro ao criar categoria");
        return null;
      }
    },
    [activeRestaurant, fetchCategories]
  );

  const updateCategory = useCallback(
    async (id: string, input: UpdateExpenseCategoryInput): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("expense_categories" as any)
          .update(input)
          .eq("id", id);

        if (error) throw error;

        toast.success("Categoria atualizada");
        await fetchCategories();
        return true;
      } catch (e) {
        console.error("Error updating expense category", e);
        toast.error("Erro ao atualizar categoria");
        return false;
      }
    },
    [fetchCategories]
  );

  const toggleCategoryActive = useCallback(
    async (id: string, isActive: boolean): Promise<boolean> => {
      return updateCategory(id, { is_active: isActive });
    },
    [updateCategory]
  );

  return {
    categories,
    allCategories,
    isLoading,
    refetch: fetchCategories,
    createCategory,
    updateCategory,
    toggleCategoryActive,
  };
}
